'use client';
import { ArticleCard } from '@pickleballinc/features/src/hub-cards/ArticleCard';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';

import { BlogPagination } from '@/modules/blog/components/BlogPagination';
import { PAGE_SIZE } from '@/modules/blog/constants';
import { ArticlePagination, ArticleTag } from '@/types/blog.types';

interface ContentLeftProps {
  articles: ArticleTag[];
  currentPage: string;
  categorySlug: string;
  featuredArticles: ArticlePagination[];
  totalPages: number;
}

export function ContentLeft({
  articles,
  currentPage,
  categorySlug,
  featuredArticles,
  totalPages,
}: ContentLeftProps) {
  const firstArticle = articles[0] || undefined;
  const secondArticle = articles[1] || undefined;
  const thirdArticle = articles[2] || undefined;

  const noPopularArticles = articles.length === 0;
  const noArticles = noPopularArticles && featuredArticles.length === 0;

  return (
    <div
      className={clsx(
        'flex size-full flex-col items-center overflow-hidden px-2 sm:px-4',
        noPopularArticles && 'pt-4'
      )}
    >
      {noArticles ? (
        <div className='my-28'>
          <NoDataFound
            title='No articles found!'
            subtitle='Currently there are no popular or featured articles in this category.'
          />
        </div>
      ) : null}
      {firstArticle ? (
        <div className='w-full max-w-[799px]'>
          <h1 className='pb-1 pl-4 pt-5 text-xl font-bold text-experiment sm:pb-5 sm:text-4xl lg:py-9 xl:pl-0'>
            Popular
          </h1>
          <CustomLink href={`/${categorySlug}/${firstArticle.slug}`}>
            <ArticleCard
              imageUrl={firstArticle.imageUrl}
              title={firstArticle.title}
              alt={firstArticle.imageAltText}
              description={firstArticle.description}
              type='latest'
            />
          </CustomLink>
        </div>
      ) : null}
      {secondArticle ? (
        <div className={clsx('mb-10 flex justify-between gap-4 sm:mb-6 sm:gap-8')}>
          {secondArticle ? (
            <CustomLink href={`/${categorySlug}/${secondArticle.slug}`}>
              <ArticleCard
                imageUrl={secondArticle.imageUrl}
                title={secondArticle.title}
                alt={secondArticle.imageAltText}
                description={secondArticle.description}
                type='popular'
              />
            </CustomLink>
          ) : null}
          {thirdArticle ? (
            <CustomLink href={`/${categorySlug}/${thirdArticle.slug}`}>
              <ArticleCard
                imageUrl={thirdArticle.imageUrl}
                title={thirdArticle.title}
                alt={thirdArticle.imageAltText}
                description={thirdArticle.description}
                type='popular'
              />
            </CustomLink>
          ) : (
            <div className='w-full max-w-[382px] sm:hidden'>
              <div className='px-4 pt-4 sm:p-0'>
                <div className={`aspect-[382/326] overflow-hidden rounded sm:rounded-2xl`} />
              </div>
            </div>
          )}
        </div>
      ) : null}
      {featuredArticles?.length > 0 && (
        <div className='flex flex-col'>
          {!noPopularArticles && (
            <div className={clsx('mb-6 h-px w-full bg-gray-200', !secondArticle && 'mt-6')} />
          )}
          <div
            className={clsx(
              'grid grid-cols-2 justify-center gap-4 sm:gap-8',
              noPopularArticles && 'sm:pt-6 xl:pt-[96px]'
            )}
          >
            {featuredArticles.map((featuredArticle) => (
              <CustomLink
                key={featuredArticle.slug}
                href={`/${categorySlug}/${featuredArticle.slug}`}
                className='flex items-start justify-center'
              >
                <ArticleCard
                  imageUrl={featuredArticle.imageUrl}
                  title={featuredArticle.title}
                  alt={featuredArticle.imageAltText}
                  description={featuredArticle.description}
                  type='popular'
                />
              </CustomLink>
            ))}
          </div>
        </div>
      )}
      {totalPages >= 1 ? (
        <div className='mb-4 flex size-full flex-col justify-end'>
          <BlogPagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalPages / PAGE_SIZE)}
            categorySlug={categorySlug}
          />
        </div>
      ) : null}
    </div>
  );
}
